import React, { useState, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useSelector } from "react-redux";
import { getVesselSOGOverLoad } from '../../../../services/vesselMachineryService';
import { generateDateTicks } from "../../../../services/dataTicks";

const SOGvsCargoLoad = ({ voyageId, selectedEngine }) => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { currentVessel } = useSelector((state) => state.vesselOverview);

  const vesselId = currentVessel?._id;
  const { selectedEngineId } = useSelector((state) => state.mainEngine);

  // Fetch data from API
  const fetchData = async (start, end) => {
    if (!vesselId || !selectedEngineId || !start || !end) return;
    const engineId = selectedEngine.toUpperCase(); 

    setLoading(true);
    try {
      const data = await getVesselSOGOverLoad(vesselId, engineId, voyageId ? null : start, voyageId ? null : end, voyageId || null);
      console.log('API Response:', data);
      setChartData(data);
    } catch (error) {
      console.error('Error fetching SOG & Load data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchData(startDate, endDate);
    }
  }, [vesselId, selectedEngine, startDate, endDate, voyageId]);

  // Prepare chart series data - both as scatter
  const sogSeries =
    chartData?.map((point) => [
      new Date(point.date).getTime(),
      point.sog,
    ]) || [];

  const loadSeries =
    chartData?.map((point) => [new Date(point.date).getTime(), point.load]) ||
    [];

  // Calculate combined performance trend line
  const calculateCombinedTrendLine = () => {
    if (chartData.length < 2) return [];

    // Calculate performance ratio (SOG per unit load) over time
    const performanceData = chartData
      .filter(point => point.load > 0) // Avoid division by zero
      .map((point, index) => {
        const performance = point.sog / point.load; // knots per % load
        return [
          new Date(point.date).getTime(), // timestamp
          performance
        ];
      });

    if (performanceData.length < 2) return [];

    // Linear regression on performance over time
    const n = performanceData.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;

    performanceData.forEach((point, index) => {
      const x = index; // time index
      const y = point[1]; // performance value
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    // Create trend line for performance
    return performanceData.map((point, index) => [
      point[0], // original timestamp
      slope * index + intercept // trend performance value
    ]);
  };

  const combinedTrendData = calculateCombinedTrendLine();

  const isChartLoading = loading;

  const dates = chartData ? chartData.map(item => item.date) : []
  let tickPositions = generateDateTicks(dates);

  const chartOptions = {
    chart: {
      type: "scatter",
      height: "500px",
      backgroundColor: "transparent",
      spacing: [20, 15, 15, 15],
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: null,
      },
      labels: {
        format: "{value:%b %e}",
        style: {
          color: "#666666",
        },
      },
      gridLineWidth: 1,
      gridLineColor: "#f0f0f0",
    },
    yAxis: [
      {
        title: {
          text: "Cargo Load (%)",
          style: {
            color: "#27ae60",
            fontSize: "12px",
          },
        },
        min: 0,
        max: 100,
        tickInterval: 10,
        gridLineWidth: 1,
        gridLineColor: "#f0f0f0",
        labels: {
          style: {
            color: "#666666",
          },
        },
      },
      {
        title: {
          text: "SOG (kts)",
          style: {
            color: "#1a5276",
            fontSize: "12px",
          },
        },
        min: 0,
        max: 20,
        tickInterval: 5,
        opposite: true,
        gridLineWidth: 0,
        labels: {
          style: {
            color: "#666666",
          },
        },
      }
    ],
    legend: {
      align: "right",
      verticalAlign: "top",
      itemStyle: {
        color: "#666666",
        fontWeight: "normal",
      },
      itemHoverStyle: {
        color: "#333333",
      },
      symbolRadius: 0,
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 6,
      shadow: true,
      useHTML: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      pointFormat:
        '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y}</span></div>',
    },
    plotOptions: {
      scatter: {
        marker: {
          radius: 5,
          symbol: "circle",
          lineWidth: 2,
          lineColor: "#ffffff",
          states: {
            hover: {
              enabled: true,
              lineColor: "#333333",
              radius: 6,
            },
          },
        },
        states: {
          hover: {
            halo: {
              size: 8,
            },
          },
        },
      },
      line: {
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            halo: {
              size: 0,
            },
          },
        },
      },
    },
    series: [
      {
        name: "Cargo Load",
        type: "scatter",
        data: loadSeries,
        color: "#27ae60",
        yAxis: 0,
        marker: {
          fillColor: "#27ae60",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " %",
        },
      },
      {
        name: "SOG",
        type: "scatter",
        data: sogSeries,
        color: "#1a5276",
        yAxis: 1,
        marker: {
          fillColor: "#1a5276",
          lineWidth: 2,
          lineColor: "#ffffff",
          radius: 5,
        },
        tooltip: {
          valueSuffix: " kts",
        },
      },
      {
        name: "Performance Trend",
        type: "line",
        data: combinedTrendData,
        color: "#e74c3c",
        yAxis: 1,
        lineWidth: 2,
        dashStyle: "Dash",
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        tooltip: {
          valueSuffix: " kts/%",
          pointFormat: '<div style="display:flex;justify-content:space-between"><span style="color:{series.color}">{series.name}:</span><span style="font-weight:700;margin-left:10px">{point.y:.3f}</span></div>'
        },
      }
    ],
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            legend: {
              align: "center",
              verticalAlign: "bottom",
              layout: "horizontal",
            },
            xAxis: {
              labels: {
                format: "{value:%b %e}",
              },
            },
          },
        },
      ],
    },
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="flex justify-between items-center">
        <h1>SOG & Cargo Load Over Time</h1>
      </div>

      {/* Chart */}
      <div className="relative">
        {isChartLoading && (
          <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
          containerProps={{
            className: "w-full",
            style: { height: "500px" },
          }}
        />
      </div>
    </div>
  );
};

export default SOGvsCargoLoad;